import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { EmployeeInquiryData } from "./types";

interface Props {
  employees: EmployeeInquiryData[];
  verticalKey: string;
  verticalName: string;
}

const FEW_EMPLOYEES_THRESHOLD = 5;
const BAR_SLOT_WIDTH = 34;
const CHART_HEIGHT = 300;

type ChartEntry = {
  name: string;
  username: string;
  email: string;
  role: string;
  verticalName: string;
  inquiries: number;
};

const getCompactBarSize = (count: number) => {
  if (count <= 1) return 34;
  if (count <= 2) return 28;
  return 22;
};

const EmployeeTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload: ChartEntry }[];
}) => {
  if (!active || !payload?.length) return null;

  const emp = payload[0].payload;

  return (
    <div className="employee-chart-tooltip">
      <div className="employee-chart-tooltip-title">{emp.name}</div>
      <div className="employee-chart-tooltip-row">
        <span>Username</span>
        <strong>{emp.username}</strong>
      </div>
      {emp.email && (
        <div className="employee-chart-tooltip-row">
          <span>Email</span>
          <strong>{emp.email}</strong>
        </div>
      )}
      <div className="employee-chart-tooltip-row">
        <span>Role</span>
        <strong>{emp.role}</strong>
      </div>
      <div className="employee-chart-tooltip-row">
        <span>Vertical</span>
        <strong>{emp.verticalName || "-"}</strong>
      </div>
      <div className="employee-chart-tooltip-row">
        <span>Assigned Inquiries</span>
        <strong>{emp.inquiries}</strong>
      </div>
    </div>
  );
};

const ChartContent = ({
  compact,
  employeeCount,
  onBarClick,
}: {
  compact: boolean;
  employeeCount: number;
  onBarClick: (bar: { payload?: ChartEntry }) => void;
}) => (
  <>
    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
    <XAxis
      dataKey="name"
      interval={0}
      angle={compact ? 0 : -35}
      textAnchor={compact ? "middle" : "end"}
      height={compact ? 48 : 70}
      tick={{ fontSize: compact ? 12 : 11, fill: "#374151" }}
      tickMargin={compact ? 8 : 4}
    />
    <YAxis allowDecimals={false} width={40} tick={{ fontSize: 11, fill: "#6b7280" }} />
    <Tooltip content={<EmployeeTooltip />} cursor={{ fill: "rgba(77, 171, 247, 0.12)" }} />
    <Bar
      dataKey="inquiries"
      fill="#4dabf7"
      name="Assigned Inquiries"
      radius={[6, 6, 0, 0]}
      maxBarSize={compact ? getCompactBarSize(employeeCount) : 22}
      barSize={compact ? getCompactBarSize(employeeCount) : 22}
      onClick={onBarClick}
      style={{ cursor: "pointer" }}
    >
      {compact && (
        <LabelList
          dataKey="inquiries"
          position="top"
          className="employee-chart-bar-label"
        />
      )}
    </Bar>
  </>
);

const EmployeeInquiriesChart: React.FC<Props> = ({
  employees,
  verticalKey,
  verticalName,
}) => {
  const navigate = useNavigate();

  if (!employees?.length) {
    return <div className="employee-chart-empty">No data available</div>;
  }

  const chartData: ChartEntry[] = employees.map(emp => ({
    name: emp.name || emp.username,
    username: emp.username,
    email: emp.email,
    role: emp.role,
    verticalName,
    inquiries: emp.assigned_inquiries || 0,
  }));

  const isCompact = chartData.length < FEW_EMPLOYEES_THRESHOLD;
  const chartWidth = chartData.length * BAR_SLOT_WIDTH;

  const handleBarClick = (bar: { payload?: ChartEntry }) => {
    const entry = bar?.payload;
    if (!entry || !verticalKey) return;

    const params = new URLSearchParams({
      assigned_to: entry.name,
    }).toString();

    navigate(`/${verticalKey}/inquiries?${params}`);
  };

  const chartMargin = isCompact
    ? { top: 24, right: 16, left: 18, bottom: 12 }
    : { top: 8, right: 16, left: 22, bottom: 64 };

  return (
    <div
      className={`employee-chart-scroll${isCompact ? " employee-chart-scroll--compact" : ""}`}
    >
      <div
        className={`employee-chart-inner${isCompact ? " employee-chart-inner--compact" : ""}`}
        style={isCompact ? { height: CHART_HEIGHT } : { width: chartWidth, height: CHART_HEIGHT }}
      >
        {isCompact ? (
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <BarChart
              data={chartData}
              margin={chartMargin}
              barCategoryGap={chartData.length <= 2 ? "35%" : "25%"}
            >
              <ChartContent
                compact
                employeeCount={chartData.length}
                onBarClick={handleBarClick}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <BarChart
            width={chartWidth}
            height={CHART_HEIGHT}
            data={chartData}
            margin={chartMargin}
          >
            <ChartContent
              compact={false}
              employeeCount={chartData.length}
              onBarClick={handleBarClick}
            />
          </BarChart>
        )}
      </div>
    </div>
  );
};

export default EmployeeInquiriesChart;
